import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';
import { aspectRatio } from 'utils/styles.utils';

import { EVENTS_PER_PAGE_COLLECTION } from 'constants/events.constant';
import { Routes } from 'constants/routes.constants';
import { useInfiniteEventsCity } from 'queries/cities/useInfiniteEventsCity';
import { useCityDetailView, useOriginClick } from 'services/datalayer/browse';
import { PageCityFragment } from 'types/api/dato-operations.types';

import DatoImage from 'components/@common/DatoImage';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import EventListItem from 'components/@events/EventListItem';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

interface Props {
  city: PageCityFragment;
}

const CityDetailContainer = ({ city }: Props) => {
  useCityDetailView({
    cityId: city.id,
    cityName: city.slug,
  });
  const handleClick = useOriginClick(city.slug, 'Collection');

  const { events, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteEventsCity({
      slug: city.slug,
      eventsPerPage: EVENTS_PER_PAGE_COLLECTION,
    });

  return (
    <>
      <PageBreadcrumbs
        items={[{ href: Routes.CollectionDetail, label: city.name }]}
      />
      {/* Changing padding from container to have paddingX: 8px on cover image*/}
      <Container sx={{ px: { xs: 1, md: 2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas: {
              xs: `'cover' 'container'`,
              md: `'container . cover'`,
            },
            gridTemplateColumns: {
              xs: '1fr',
              md: '6fr 1fr 5fr',
            },
            mt: { md: 3 },
            mb: { md: 15 },
          }}
        >
          {/* Re adding paddingX: 8px (16px total) for normal container padding on other items*/}
          <Stack
            sx={{
              gridArea: 'container',
              px: { xs: 1, md: 0 },
            }}
          >
            <Stack
              sx={{
                mb: 3,
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  textTransform: 'uppercase',
                }}
              >
                {city.name}
              </Typography>
            </Stack>

            {!!events?.length ? (
              <>
                <Stack
                  sx={{
                    gap: 2,
                    pb: { xs: 2 },
                  }}
                >
                  {events.map((event, index) => (
                    <EventListItem
                      key={event.id}
                      component={LinkUnstyled}
                      onClick={handleClick}
                      href={replacePathParams(Routes.EventDetail, {
                        slug: event.slug,
                      })}
                    >
                      <EventListItem.Cover
                        cover={event.cover}
                        priority={index < 5}
                      />
                      <EventListItem.Content>
                        <EventListItem.Title>{event.title}</EventListItem.Title>

                        <EventListItem.Date
                          startDatetime={event.startDatetime}
                          endDatetime={event.endDatetime}
                          timezone={event.timezone}
                        />
                        <EventListItem.City city={event.venue.city.name} />
                      </EventListItem.Content>
                    </EventListItem>
                  ))}
                </Stack>

                <Box
                  sx={{
                    textAlign: 'center',
                    pt: 3,
                  }}
                >
                  {hasNextPage && (
                    <Button
                      onClick={() => {
                        fetchNextPage();
                      }}
                      loading={isFetching}
                      variant="outlined"
                      color="secondary"
                    >
                      <FormattedMessage id="events_overview.events.load_more" />
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <Stack
                sx={{
                  gap: 2,
                  pb: { xs: 2 },
                }}
              >
                <Typography variant="body1">
                  <FormattedMessage id="city.detail.events.empty.state.title" />
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  component={LinkUnstyled}
                  href={Routes.Home}
                  fullWidth
                >
                  <FormattedMessage id="error.back.home.link" />
                </Button>
              </Stack>
            )}
          </Stack>

          {city.cover && (
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 6,
                gridArea: 'cover',
                mb: { xs: 2, md: 0 },
                ...aspectRatio(1, 1),
              }}
            >
              <DatoImage
                data={city.cover}
                priority
                fill
                style={{ objectFit: 'cover' }}
                sizes="512px"
              />
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default CityDetailContainer;
